/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ComponentType, DetailedHTMLProps, HTMLAttributes, useEffect, useState } from 'react';
import manifest from '@/docs/manifest.json';
import * as React from 'react';
import { PreClipBoard } from '@/pages/DocsPage/DocsLayout/PreClipBoard';

import { UseMdxComponents } from '@mdx-js/mdx';
import { TitleAnchorLink } from '@/pages/DocsPage/DocsLayout/TitleAnchorLink';
import { useStoreContext } from '@/contexts/StoreContext';

type MDXComponentProps = ReturnType<UseMdxComponents>;

type MDXPComponentType = ComponentType<{ components: MDXComponentProps }>;

type DocModule = { default: MDXPComponentType };

export type DocManifestType = (typeof manifest)[0];

type DocManifestTypeWithModules = DocManifestType & { module: React.ReactNode };

type ModuleImportsPromise = Record<string, () => Promise<DocModule>>;

const modules = import.meta.glob('@/docs/content/**/*') as ModuleImportsPromise;

const components = {
  pre: (props: DetailedHTMLProps<HTMLAttributes<HTMLPreElement>, HTMLPreElement>) => <PreClipBoard {...props} />,
  h1: (props: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
    <TitleAnchorLink tagName={'h1'} {...props}>
      {props.children}
    </TitleAnchorLink>
  ),
  h2: (props: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
    <TitleAnchorLink tagName={'h2'} {...props}>
      {props.children}
    </TitleAnchorLink>
  ),
  h3: (props: DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
    <TitleAnchorLink tagName={'h3'} {...props}>
      {props.children}
    </TitleAnchorLink>
  )
};

export const useDocLoader = () => {
  const [loading, setLoading] = useState(true);
  const { externalServiceConfig } = useStoreContext();

  const [MDXComponents, setMDXComponents] = useState<Array<DocManifestTypeWithModules>>([]);

  useEffect(() => {
    const loadDocs = async () => {
      setLoading(true);
      try {
        const filePromises: Array<Promise<DocManifestTypeWithModules>> = manifest.map(async (man) => {
          const moduleTargeted = Object.entries(modules).find(([k]) => k.endsWith(man.path));
          if (!moduleTargeted) {
            throw new Error(`${man.path}, is not defined`);
          }
          const _module = await moduleTargeted[1]();
          /* @vite-ignore */
          const module: DocModule['default'] = _module.default;
          const Comp = module;
          const r = <Comp components={components as MDXComponentProps} />;

          return { ...man, module: r } satisfies DocManifestTypeWithModules;
        });
        let resolvedFiles = await Promise.all(filePromises);
        resolvedFiles = resolvedFiles.filter((file) => {
          return externalServiceConfig?.spaces?.serviceUp || file.feature !== 'spaces';
        });
        setMDXComponents(resolvedFiles);
        setLoading(false);
      } catch (error) {
        console.error('Error loading documents:', error);
      }
    };
    loadDocs();
  }, [externalServiceConfig]);

  return { loading, MDXComponents, manifest };
};
